// vg-binding: P7.D-41
// vg-binding: P7.D-49
// vg-binding: P7.D-50
// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/pages/Earnings.tsx
//
// Phase 7 Wave 11 Task 38 — Earnings page.

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader, Pagination } from '@printway/ui';

import { EarningsChart } from '../features/earnings/EarningsChart.js';
import { EarningsKpiCards } from '../features/earnings/EarningsKpiCards.js';
import { TransactionsTable } from '../features/earnings/TransactionsTable.js';
import {
  useTransactions,
  useWalletSummary,
} from '../features/wallet/api.js';
import { useTranslation } from '../lib/i18n/index.js';

const TX_PAGE_LIMIT = 20;

export function EarningsPage(): JSX.Element {
  const { t } = useTranslation('wallet');
  const summaryQ = useWalletSummary();
  const [page, setPage] = useState(1);
  const txQ = useTransactions({ limit: TX_PAGE_LIMIT, page });

  const updatedAtLabel = summaryQ.dataUpdatedAt
    ? new Date(summaryQ.dataUpdatedAt).toLocaleTimeString(undefined, { hour12: false })
    : '—';

  return (
    <main
      data-testid="earnings-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <PageHeader
        title={t('earningsPage.title')}
        meta={
          <p
            data-testid="earnings-freshness-pill"
            className="text-xs text-pw-text-muted"
          >
            {t('earningsPage.updatedAt', { time: updatedAtLabel })}
            {summaryQ.isFetching ? t('earningsPage.refreshing') : ''}
          </p>
        }
        actionsAlign="center"
        actions={
          <>
            <a
              // UAT #2168 — same trap as #2045: an <a href> bypasses the
              // csrfFetch VITE_API_BASE_URL rewrite, so the relative URL hit
              // the SPA shell (404). Prepend the API base explicitly.
              href={`${(import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''}/api/v1/vendor/wallet/transactions/export`}
              data-testid="earnings-export-csv"
              className="rounded-md border border-pw-border bg-pw-surface px-3 py-1.5 text-sm font-medium text-pw-text hover:bg-pw-bg"
            >
              {t('earningsPage.exportCsv')}
            </a>
            <Link
              to="/payouts"
              className="rounded-md bg-pw-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-pw-primary-strong"
            >
              {t('earningsPage.withdrawCta')}
            </Link>
          </>
        }
      />

      <EarningsKpiCards summary={summaryQ.data} isLoading={summaryQ.isLoading} />
      <EarningsChart rows={txQ.data?.rows ?? []} isLoading={txQ.isLoading} />
      <section className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('earningsPage.transactionsSection')}</h3>
        <TransactionsTable rows={txQ.data?.rows ?? []} isLoading={txQ.isLoading} />
        <Pagination
          currentPage={page}
          pageCount={txQ.data?.page_count ?? 0}
          totalRows={txQ.data?.total ?? 0}
          testId="earnings-tx-pagination"
          disabled={txQ.isFetching}
          onChange={setPage}
        />
      </section>
    </main>
  );
}

export default EarningsPage;
